import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
    catchError,
    combineLatestWith,
    concatMap,
    delay,
    exhaustMap,
    of,
    switchMap,
} from 'rxjs';
import { ProfileGeneralActions } from '../../../profile/store/actions/actions-type.action';
import { TeamActions } from '../../../team/store/actions/actions-type.action';
import { TournamentGeneralActions } from '../../../tournament/store/actions/actions-type.action';
import { SnackBarType } from '../../components/snack-bar/snack-bar.component';
import { BadResponseWithError } from '../../models/bad-response-with-error.interface';
import { UploadMediaDomain } from '../../models/media.model';
import { MediaService } from '../../services/media.service';
import { NotificationsService } from '../../services/notifications.service';
import { UploadMediaActions } from '../actions/actions-type.action';

@Injectable()
export class MediaEffect {
    private actions$ = inject(Actions);
    private mediaService = inject(MediaService);
    private notificationsService = inject(NotificationsService);

    uploadImage$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UploadMediaActions.uploadImageFile),
            switchMap(({ payload }) =>
                this.mediaService
                    .requestUploadToken({
                        domain: payload.domain,
                        mediaType: payload.mediaType,
                        targetId: payload.targetId,
                    })
                    .pipe(combineLatestWith(of(payload)))
            ),
            switchMap(([token, payload]) =>
                this.mediaService.uploadImage(payload, token).pipe(
                    delay(1000),
                    concatMap(() => {
                        const sideEffects = this.getSideEffectsByDomain(
                            payload.domain,
                            payload.targetId
                        );

                        payload.onSuccess();

                        return [
                            UploadMediaActions.uploadImageFileSuccess(),
                            ...sideEffects,
                        ];
                    }),
                    catchError((response: BadResponseWithError) => {
                        this.notificationsService.openSnackBar({
                            title: 'Upload error',
                            message: response.error.message,
                            type: SnackBarType.Error,
                            error: response.error,
                        });
                        return of(
                            UploadMediaActions.uploadImageFileFailure({
                                payload: response,
                            })
                        );
                    })
                )
            )
        )
    );

    removeImage$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UploadMediaActions.removeImageFile),
            exhaustMap(({ payload }) =>
                this.mediaService.removeImageFile(payload).pipe(
                    delay(300),
                    concatMap(() => {
                        const sideEffects = this.getSideEffectsByDomain(
                            payload.domain,
                            payload.targetId
                        );

                        return [
                            UploadMediaActions.removeImageFileSuccess(),
                            ...sideEffects,
                        ];
                    }),
                    catchError((response: BadResponseWithError) => {
                        this.notificationsService.openSnackBar({
                            title: 'Error',
                            message: response.error.message,
                            type: SnackBarType.Error,
                            error: response.error,
                        });
                        return of(
                            UploadMediaActions.removeImageFileFailure({
                                payload: response,
                            })
                        );
                    })
                )
            )
        )
    );

    private getSideEffectsByDomain(
        domain: UploadMediaDomain,
        targetId: string
    ) {
        const sideEffects: any[] = [];
        if (domain === UploadMediaDomain.Team) {
            sideEffects.push(
                TeamActions.profileGetTeamInfo({
                    payload: targetId,
                })
            );
        }

        if (domain === UploadMediaDomain.Profile) {
            sideEffects.push(
                ProfileGeneralActions.fetchPlayerData({
                    payload: {
                        playerId: targetId,
                    },
                })
            );
            sideEffects.push(ProfileGeneralActions.fetchCurrentPlayerData());
        }

        if (domain === UploadMediaDomain.Tournament) {
            sideEffects.push(
                TournamentGeneralActions.getTournamentById({
                    payload: targetId,
                })
            );
        }

        return sideEffects;
    }
}
